import { format } from 'date-fns';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import db_config from '../../configs/db_config';
import { TABS } from '../../consts';
import {
  CategoriesType,
  FoodDetailItem,
  HomeMenuCategoryWithFoods,
  RestaurantWithFoodType,
  UserMenuByCategoryId,
  UserMenuCategoriesById,
  UserMenuCategoryDoc,
  UserMenuContextValue,
  UserMenuFoodRef,
  listRestaurantType,
} from '../../interfaces/types';
import { db } from '../../libs/firebase';
import { isRestaurantOnline } from '../../utils';
import { useSystemContext } from '../SystemContext/SystemContext';

const getStadiumManageFoodDetails = () => {
  return db.collection(db_config.DB_TABLE_ALL_FOOD_DETAILS);
};

const getUerMenuCategories = () => {
  return db.collection(db_config.DB_TABLE_USER_MENU_CATEGORIES);
};

const getRestaurantConfig = () => {
  return db.collection(db_config.DB_TABLE_RESTAURANT_CONFIG);
};

const UserMenuContext = createContext<UserMenuContextValue>({
  listRestaurantData: [],
  listRestaurantWithFood: [],
  loading: true,
  loadingFoods: true,
  findRestaurantById: () => undefined,
  setFetchData: () => { },
  setListRestaurantWithFood: () => { },
  activeTab: TABS.SEAT,
  setActiveTab: () => { },
  restaurantFoodMap: {},
  userMenuByCategoryId: {},
  userMenuCategoriesById: {},
  homeMenuCategories: [],
  suggestedFoods: [],
  getFoodOrder: () => null,
  loadingFoodTrucks: true,
});

const UserMenuProvider = ({ children }: { children: ReactNode }) => {
  const { stadiumInfo, seatInfo, menuType } = useSystemContext();
  const isPreOrderMenuType = menuType === 'regular_pre_order' || menuType === 'special_pre_order';
  const [activeTab, setActiveTab] = useState(TABS.HOME);
  const [loading, setLoading] = useState(true);
  const [loadingFoods, setLoadingFoods] = useState(true);
  const [fetchData, setFetchData] = useState(false);
  const [listRestaurantData, setListRestaurantData] = useState<listRestaurantType[]>([]);
  const [listRestaurantWithFood, setListRestaurantWithFood] = useState<RestaurantWithFoodType[]>([]);
  const [restaurantFoodMap, setRestaurantFoodMap] = useState<{ [key: string]: FoodDetailItem[] }>({});
  const [userMenuByCategoryId, setUserMenuByCategoryId] = useState<UserMenuByCategoryId>({});
  const [userMenuCategoriesById, setUserMenuCategoriesById] = useState<UserMenuCategoriesById>({});
  const [availableFoodTrucks, setAvailableFoodTrucks] = useState<string[] | null>(null);
  const [loadingFoodTrucks, setLoadingFoodTrucks] = useState<boolean>(true);

  const getFoodOrder = useCallback(
    (categoryId: string, foodId: string) => {
      return userMenuByCategoryId[categoryId]?.foodsById?.[foodId]?.order ?? null;
    },
    [userMenuByCategoryId]
  );

  const suggestedFoods = useMemo(() => {
    const seen = new Set<string>();
    const scored: Array<{ foodId: string; bannerOrder: number; categoryOrder: number; foodOrder: number }> = [];

    Object.entries(userMenuByCategoryId).forEach(([catId, cat]) => {
      Object.entries(cat.foodsById).forEach(([foodKey, f]) => {
        if (!f.enabled || !f.banner) return;
        if (seen.has(f.id)) return;
        seen.add(f.id);
        scored.push({
          foodId: f.id,
          bannerOrder: typeof f.banner_order === 'number' ? f.banner_order : 9999,
          categoryOrder: cat.order ?? 0,
          foodOrder: f.order ?? 0,
        });
      });
    });

    // Sort by explicit banner_order first; fall back to category+food position
    scored.sort((a, b) => a.bannerOrder - b.bannerOrder || a.categoryOrder - b.categoryOrder || a.foodOrder - b.foodOrder);

    const allFoods: FoodDetailItem[] = Object.values(restaurantFoodMap).flat();
    const byAnyId = new Map<string, FoodDetailItem>();
    allFoods.forEach((f) => {
      if (f.id) byAnyId.set(f.id, f);
      if (f.food_id) byAnyId.set(f.food_id, f);
    });

    const result = scored
      .map((s) => {
        const found = byAnyId.get(s.foodId);
        return found;
      })
      .filter((f): f is FoodDetailItem => !!f)
      .filter((f) => {
        // Available (enabled) only applies to regular/special order
        // For pre-order menus, check enabled_pre_order instead
        if (isPreOrderMenuType) return f.enabled_pre_order === true;
        return !!f.enabled;
      })
      .filter((f) => {
        const restaurant = listRestaurantData.find((r) => r.id === f.restaurantId);
        return restaurant ? isRestaurantOnline(restaurant) : false;
      });

    return result;
  }, [restaurantFoodMap, userMenuByCategoryId, listRestaurantData, isPreOrderMenuType]);

  const allFoodsByAnyId = useMemo(() => {
    const allFoods: FoodDetailItem[] = Object.values(restaurantFoodMap).flat();
    const byAnyId = new Map<string, FoodDetailItem>();
    allFoods
      .filter((f) => {
        const restaurant = listRestaurantData.find((r) => r.id === f.restaurantId);
        return restaurant ? isRestaurantOnline(restaurant) : false;
      })
      .forEach((f) => {
        if (f.id) byAnyId.set(f.id, f);
        if (f.food_id) byAnyId.set(f.food_id, f);
      });
    return byAnyId;
  }, [restaurantFoodMap, listRestaurantData]);

  const homeMenuCategories: HomeMenuCategoryWithFoods[] = useMemo(() => {
    const categories = Object.values(userMenuCategoriesById)
      .filter((c) => !!c.enabled)
      .map((cat) => {
        const userMenuOrder = userMenuByCategoryId[cat.id]?.order;
        return {
          ...cat,
          order: userMenuOrder !== undefined ? userMenuOrder : (cat.order ?? 0),
        };
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return categories
      .map((cat) => {
        const foodsMeta = userMenuByCategoryId[cat.id]?.foodsById;
        const foods = (cat.foodIds || [])
          .map((foodRefId) => {
            const meta = foodsMeta?.[foodRefId];
            if (foodsMeta && !meta?.enabled) return null;
            const food = allFoodsByAnyId.get(foodRefId);
            if (!food) return null;
            // Available (enabled) only hides food in regular/special menus
            // For pre-order menus, only explicitly enabled foods are shown
            if (isPreOrderMenuType) {
              if (food.enabled_pre_order !== true) return null;
            } else {
              if (!food.enabled) return null;
            }
            return {
              ...food,
              banner: meta?.banner || false,
              banner_order: meta?.banner_order,
            } as FoodDetailItem;
          })
          .filter((f): f is FoodDetailItem => !!f)
          .sort((a, b) => {
            const ao = foodsMeta?.[a.id]?.order ?? foodsMeta?.[a.food_id]?.order;
            const bo = foodsMeta?.[b.id]?.order ?? foodsMeta?.[b.food_id]?.order;
            if (ao == null && bo == null) return 0;
            if (ao == null) return 1;
            if (bo == null) return -1;
            return ao - bo;
          });

        return {
          id: cat.id,
          title: cat.title,
          order: cat.order ?? 0,
          foods,
        };
      })
      .filter((c) => c.foods.length > 0);
  }, [allFoodsByAnyId, isPreOrderMenuType, userMenuByCategoryId, userMenuCategoriesById]);

  const fetchUserMenu = useCallback(() => {
    try {
      if (!stadiumInfo?.id) return;

      let activeUnsub: null | (() => void) = null;
      let cancelled = false;

      const attachListener = (type: string) => {
        if (cancelled) return;
        activeUnsub = db
          .collection(db_config.DB_TABLE_USER_MENU_LIST)
          .doc(stadiumInfo.id)
          .collection(type)
          .onSnapshot((snap: any) => {
            const next: UserMenuByCategoryId = {};
            snap.docs.forEach((doc: any) => {
              const data = doc.data() || {};
              const foodIds: any[] = Array.isArray(data.food_ids) ? data.food_ids : [];
              const foodsById: Record<string, UserMenuFoodRef> = {};

              foodIds.forEach((f: any) => {
                if (!f?.id) return;
                const recommendedFood: any[] = Array.isArray(f.recommended_food) ? f.recommended_food : [];
                foodsById[f.id] = {
                  id: f.id,
                  order: typeof f.order === 'number' ? f.order : 0,
                  enabled: !!f.enabled,
                  banner: !!f.banner,
                  banner_order: typeof f.banner_order === 'number' ? f.banner_order : undefined,
                  recommended_food: recommendedFood.map((rf: any) => ({
                    id: rf.id || '',
                    order: typeof rf.order === 'number' ? rf.order : 0,
                    enabled: !!rf.enabled,
                  })),
                };
              });

              next[doc.id] = {
                order: typeof data.order === 'number' ? data.order : 0,
                foodsById,
              };
            });
            setUserMenuByCategoryId(next);
          });
      };

      db.collection(db_config.DB_TABLE_USER_MENU_LIST)
        .doc(stadiumInfo.id)
        .collection(menuType)
        .limit(1)
        .get()
        .then((probe) => {
          if (!cancelled && !probe.empty) {
            attachListener(menuType);
          } else {
            setUserMenuByCategoryId({});
          }
        })
        .catch(() => {
          setUserMenuByCategoryId({});
        });

      return () => {
        cancelled = true;
        if (activeUnsub) activeUnsub();
      };
    } catch (e) {
      setUserMenuByCategoryId({});
      return;
    }
  }, [stadiumInfo?.id, menuType]);

  const fetchUserMenuCategories = useCallback(() => {
    try {
      if (!stadiumInfo?.id) return;
      const unsub = getUerMenuCategories()
        .where('stadium_id', '==', stadiumInfo.id)
        .onSnapshot((snap: any) => {
          const next: UserMenuCategoriesById = {};
          snap.docs.forEach((doc: any) => {
            const data = doc.data() || {};
            const categoryId = data.id || doc.id;
            if (!categoryId) return;
            const item: UserMenuCategoryDoc = {
              id: categoryId,
              stadium_id: data.stadium_id ?? '',
              title: data.title ?? '',
              order: typeof data.order === 'number' ? data.order : 0,
              enabled: !!data.enabled,
              foodIds: Array.isArray(data.foodIds) ? data.foodIds : [],
            };
            next[categoryId] = item;
          });
          setUserMenuCategoriesById(next);
        });
      return () => unsub && unsub();
    } catch (e) {
      setUserMenuCategoriesById({});
      return;
    }
  }, [stadiumInfo?.id]);

  const fetchAvailableFoodTrucks = useCallback(() => {
    if (!stadiumInfo?.id) return;

    const rawDate = seatInfo?.order_date;
    const targetDate = rawDate
      ? format(new Date(rawDate), 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd');

    const unsub = db
      .collection(db_config.DB_TABLE_STADIUMS_GAMEDAYS)
      .doc(stadiumInfo.id)
      .collection('gamedays')
      .where('date', '==', targetDate)
      .onSnapshot(
        (snap) => {
          if (snap.empty) {
            // No gameday doc for this date → no restriction, show all
            setAvailableFoodTrucks(null);
          } else {
            const data = snap.docs[0].data();
            const trucks: string[] = Array.isArray(data.available_food_trucks) ? data.available_food_trucks : [];
            setAvailableFoodTrucks(trucks);
          }
          setLoadingFoodTrucks(false);
        },
        () => {
          // On error, fall back to showing all
          setAvailableFoodTrucks(null);
          setLoadingFoodTrucks(false);
        }
      );

    return () => unsub();
  }, [stadiumInfo?.id, seatInfo?.order_date]);

  const fetchRestaurantData = useCallback(() => {
    try {
      setLoading(true);
      if (!stadiumInfo?.id) {
        return;
      }

      const unsubscribeCategories = getUerMenuCategories().onSnapshot((querycategories) => {
        const rawCategoryDocs: CategoriesType[] = querycategories.docs.map((doc) => ({
          id: doc.id,
          category_show: doc.data().enabled,
          foooIds: doc.data().foodIds,
          order: doc.data().order,
          stadium_id: doc.data().stadium_id,
          title: doc.data().title,
        }));

        const hasUserMenu = Object.keys(userMenuByCategoryId).length > 0;
        const categoryDocs: CategoriesType[] = rawCategoryDocs
          .filter((c) => (hasUserMenu ? !!userMenuByCategoryId[c.id] : true))
          .map((c) => ({
            ...c,
            order: hasUserMenu ? (userMenuByCategoryId[c.id]?.order ?? c.order) : c.order,
          }));

        const unsubscribeRestaurant = getRestaurantConfig()
          .where('stadium_id', '==', stadiumInfo.id)
          .where('status', '==', 'active')
          .onSnapshot(async (queryRestaurantConfig) => {
            const listRestaurant = await Promise.all(
              queryRestaurantConfig.docs.map(async (doc) => {
                const data = doc.data();

                const restaurant: listRestaurantType = {
                  id: doc.id,
                  name: data.name,
                  region: data.region,
                  location_id: data.location_id ?? '',
                  merchant_id: data.merchant_id ?? '',
                  restaurant_profile_pic: data.restaurant_profile_pic,
                  categories: categoryDocs.filter((category) => category.category_show),
                  opening_schedule: data.opening_schedule,
                  isOnline: data.isOnline,
                  restaurant_open_id: data.restaurant_open_id,
                  restaurant_open_state: data.restaurant_open_state,
                  isStadiumPromotion: stadiumInfo.isStadiumPromotion ?? false,
                  promotion_restaurant_id: stadiumInfo.promotion_restaurant_id ?? '',
                  sold: data.sold ?? 0,
                  preorder_rules: data.preorder_rules,
                  enabled_pre_order: data.enabled_pre_order,
                  cancellation_alert: data.cancellation_alert,
                };

                return restaurant;
              })
            );

            listRestaurant.sort((a, b) => b.sold - a.sold);

            // Filter by gameday available food trucks
            const filteredRestaurants =
              availableFoodTrucks === null
                ? listRestaurant // no gameday doc → show all
                : listRestaurant.filter((r) => availableFoodTrucks.includes(r.id));

            if (filteredRestaurants.length === 0) {
              await addLogs('list restaurants empty after food truck filter');
            }
            setListRestaurantData(filteredRestaurants);
            setLoading(false);
          });

        return () => {
          unsubscribeRestaurant && unsubscribeRestaurant();
        };
      });

      return () => {
        unsubscribeCategories && unsubscribeCategories();
      };
    } catch (error) {
      setLoading(false);
    }
  }, [stadiumInfo?.id, userMenuByCategoryId, availableFoodTrucks]);

  const fetchFoodsData = useCallback(() => {
    setLoadingFoods(true);
    if (listRestaurantData.length > 0) {
      const restaurantFoodMap: Record<string, FoodDetailItem[]> = {};
      const addedFoodIds: Record<string, boolean> = {};

      listRestaurantData.forEach((restaurant) => {
        const categoryIds: string[] = Array.isArray(restaurant.categories) && restaurant.categories.length > 0 ? restaurant.categories.map((c) => c.id) : [];

        if (categoryIds.length === 0) return;

        const foodChunks = chunkArray(categoryIds, 20);

        foodChunks.forEach((chunk) => {
          getStadiumManageFoodDetails()
            .where('restaurantId', '==', restaurant.id)
            .where('categories_menu_ids', 'array-contains-any', chunk)
            .orderBy('create_time', 'desc')
            .onSnapshot(
              (querySnapshot) => {
                querySnapshot.docChanges().forEach((change) => {
                  const foodData = change.doc.data();
                  const foodDetail: FoodDetailItem = {
                    enabled: foodData.enabled,
                    enabled_note: foodData.enabled_note,
                    isEnableStockCount: foodData.isEnableStockCount,
                    description: foodData.description,
                    food_show_status: foodData.food_show_status,
                    unit_price: foodData.unit_price,
                    restaurantId: foodData.restaurantId,
                    customize_group_settings: foodData.customize_group_settings,
                    id: foodData.id,
                    price: foodData.price,
                    image_url: foodData.image_url,
                    name: foodData.name,
                    food_id: foodData.food_id,
                    categories: foodData.categories_menu_ids,
                    sku: foodData.sku,
                    tax_rate: foodData.tax_rate,
                    food_type: foodData.food_type,
                    is_alcoholic: foodData.is_alcoholic || false,
                    sold: foodData.sold ?? 0,
                    enabled_pre_order: foodData.enabled_pre_order,
                    pre_order_quantity: foodData.pre_order_quantity,
                    pre_order_sold: foodData.pre_order_sold ?? {},
                    foodtruck_recommended_food: foodData.foodtruck_recommended_food || [],
                  };

                  switch (change.type) {
                    case 'added':
                      if (!addedFoodIds[foodDetail.id]) {
                        if (!restaurantFoodMap[foodDetail.restaurantId]) {
                          restaurantFoodMap[foodDetail.restaurantId] = [];
                        }
                        restaurantFoodMap[foodDetail.restaurantId].push(foodDetail);
                        addedFoodIds[foodDetail.id] = true;
                      }
                      break;
                    case 'modified':
                      if (restaurantFoodMap[foodDetail.restaurantId]) {
                        restaurantFoodMap[foodDetail.restaurantId] = restaurantFoodMap[foodDetail.restaurantId].map((item) =>
                          item.id === foodDetail.id ? foodDetail : item
                        );
                      }
                      break;
                    case 'removed':
                      if (restaurantFoodMap[foodDetail.restaurantId]) {
                        restaurantFoodMap[foodDetail.restaurantId] = restaurantFoodMap[foodDetail.restaurantId].filter((item) => item.id !== foodDetail.id);
                        delete addedFoodIds[foodDetail.id];
                      }
                      break;
                    default:
                      break;
                  }
                });
                setRestaurantFoodMap({ ...restaurantFoodMap });
              },
              (error) => {
                console.error('Error fetching food snapshot:', error);
              }
            );
        });
      });
    } else {
      setLoadingFoods(false);
    }
  }, [listRestaurantData]);

  function chunkArray(categories: any, size: number) {
    const chunkedArr = [];
    let index = 0;
    while (index < categories.length) {
      chunkedArr.push(categories.slice(index, size + index));
      index += size;
    }
    return chunkedArr;
  }

  const addLogs = async (error: string) => {
    try {
      const docRef = db.collection('logs').doc('error');
      const errorEntry = {
        message: error,
        timestamp: new Date().toISOString(),
      };

      await docRef.set(
        {
          data: firebase.firestore.FieldValue.arrayUnion(errorEntry),
        },
        { merge: true }
      );
    } catch (err) {
      console.error('error:', err);
    }
  };

  const mergeRestaurantData = async () => {
    const updatedListRestaurantData = await Promise.all(
      listRestaurantData.map(async (restaurant) => {
        if (restaurant.id in restaurantFoodMap) {
          const filteredFoods = restaurantFoodMap[restaurant.id].filter((food) =>
            food.categories.some((foodCategory) => restaurant.categories.some((restaurantCategory) => restaurantCategory.id === foodCategory))
          );

          const hasUserMenu = Object.keys(userMenuByCategoryId).length > 0;

          const updatedFoodDetails = filteredFoods
            .map((food) => {
              if (!hasUserMenu) {
                return { ...food, sold: food.sold ?? 0 };
              }

              const nextCategories = (food.categories || []).filter((catId) => {
                const foodsById = userMenuByCategoryId[catId]?.foodsById;
                const meta = foodsById?.[food.id] || (food.food_id ? foodsById?.[food.food_id] : undefined);
                return !!meta?.enabled;
              });

              return {
                ...food,
                categories: nextCategories,
                sold: food.sold ?? 0,
              };
            })
            .filter((food) => (food.categories?.length ?? 0) > 0);

          return { ...restaurant, food_details: updatedFoodDetails };
        } else {
          return { ...restaurant, food_details: [] };
        }
      })
    );

    setListRestaurantWithFood(updatedListRestaurantData);
    setLoadingFoods(false);
  };

  const findRestaurantById = (id: string): RestaurantWithFoodType | undefined => {
    const restaurant = listRestaurantWithFood.find((restaurant) => restaurant.id === id);
    return restaurant ? restaurant : undefined;
  };

  useEffect(() => {
    fetchRestaurantData();
  }, [fetchRestaurantData, stadiumInfo?.id, fetchData]);

  useEffect(() => {
    const unsub = fetchUserMenu();
    return () => {
      typeof unsub === 'function' && unsub();
    };
  }, [fetchUserMenu]);

  useEffect(() => {
    const unsub = fetchUserMenuCategories();
    return () => {
      typeof unsub === 'function' && unsub();
    };
  }, [fetchUserMenuCategories]);

  useEffect(() => {
    const unsub = fetchAvailableFoodTrucks();
    return () => {
      typeof unsub === 'function' && unsub();
    };
  }, [fetchAvailableFoodTrucks]);

  useEffect(() => {
    fetchFoodsData();
  }, [listRestaurantData]);

  useEffect(() => {
    const fetchData = async () => {
      await mergeRestaurantData();
    };

    fetchData();
  }, [restaurantFoodMap]);

  const onChangeTab = (tab: TABS, force: boolean = false) => {
    if (!force && (!seatInfo?.id || !seatInfo?.isConfirmed || !stadiumInfo?.id)) {
      setActiveTab(TABS.SEAT);
    } else {
      setActiveTab(tab);
    }
  };

  return (
    <UserMenuContext.Provider
      value={{
        loading,
        loadingFoods,
        listRestaurantData,
        listRestaurantWithFood,
        findRestaurantById,
        setFetchData,
        setListRestaurantWithFood,
        activeTab,
        setActiveTab: onChangeTab,
        restaurantFoodMap,
        userMenuByCategoryId,
        userMenuCategoriesById,
        homeMenuCategories,
        suggestedFoods,
        getFoodOrder,
        loadingFoodTrucks,
      }}
    >
      {children}
    </UserMenuContext.Provider>
  );
};

const useUserMenuContext = () => {
  const context = useContext(UserMenuContext);
  return context;
};

export { UserMenuProvider, useUserMenuContext };
